// fetchLivestreamDetail.js
'use strict';

import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

import handleRealtimeEvent from '../action/handleRealtimeEvent.js';

import fetch from '../resource/customFetch.js';
import { API_V2 } from '../resource/resourceUrlTypeConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import { RealtimeEvent } from '../resource/realtimeConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch livestream detail
 * @kind action
 * @param {object} options - action options.
 * @param {string} options.streamId - stream id.
 * @return {Promise} Action promise.
 * @see https://api-v2.swag.live/docs#/livestream/get_livestream_session_v2_streams__streamer_id__get
 */
const fetchLivestreamDetail =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const networkingSelectPath = ['fetch', 'livestream', streamId];
    const token = getMeData(getState(), 'token');
    const isFetching = getNetworkingData(
      getState(),
      networkingSelectPath,
      'isFetching'
    );

    if (!streamId || isFetching) {
      return dispatch({ type: '' });
    }

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
      },
    };
    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    const fetchUrl = getResourceUrl({
      endpoint: `/streams/${streamId}`,
      resourceType: API_V2,
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);

      const is404 = 404 === response.status;

      if (!response.ok && !is404) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }

      // status 404 means the stream is offline.
      if (is404) {
        dispatch(
          handleRealtimeEvent({
            id: streamId,
            event: RealtimeEvent.LIVESTREAM_SESSION_ENDED,
            data: {
              timestamp: getCurrentUnixTimestamp({
                offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
              }),
            },
          })
        );
        return dispatch({
          type: SET_NETWORKING_SUCCESS,
          payload: { selectPath: networkingSelectPath },
        });
      }

      const data = await response.json();

      dispatch(
        handleRealtimeEvent({
          id: streamId,
          event: RealtimeEvent.LIVESTREAM_SESSION_UPDATED,
          data,
        })
      );

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchLivestreamDetail;
